// src/pages/auth/ResetPassword.jsx
import React, { useEffect, useState } from 'react';
import { auth } from '../../firebaseConfig';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
  Image, // Import the Image component
} from '@chakra-ui/react';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const oobCode = searchParams.get('oobCode');
  const [checking, setChecking] = useState(true);
  const [codeValid, setCodeValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkCode = async () => {
      if (!oobCode) {
        setChecking(false);
        setCodeValid(false);
        return;
      }
      try {
        const emailFromCode = await verifyPasswordResetCode(auth, oobCode);
        if (!cancelled) {
          setEmail(emailFromCode || '');
          setCodeValid(true);
        }
      } catch (err) {
        console.error('verifyPasswordResetCode error:', err);
        if (!cancelled) {
          setCodeValid(false);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    checkCode();
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !password2) {
      toast({
        title: 'Enter a new password',
        description: 'Please fill both password fields.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (password !== password2) {
      toast({
        title: 'Passwords do not match',
        description: 'Please re-enter the same password.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setSubmitting(true);
      await confirmPasswordReset(auth, oobCode, password);
      setCompleted(true);
      toast({
        title: 'Password updated',
        description: 'Your password has been reset successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      console.error('confirmPasswordReset error:', err);
      toast({
        title: 'Could not reset password',
        description:
          'The link may be expired or invalid. Please request a new reset email.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const goToLogin = () => navigate('/login');

  const containerProps = {
    p: 8,
    maxW: "md",
    w: "full",
    borderWidth: 1,
    borderRadius: "lg",
    boxShadow: "lg",
    bg: "white",
  };

  if (checking) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Box {...containerProps}>
          <Image src="/images/logo.png" alt="FixiHub Logo" maxW="240px" mb={6} mx="auto" />
          <Heading size="md" textAlign="center">
            Checking your link…
          </Heading>
        </Box>
      </Center>
    );
  }

  if (!codeValid) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Box {...containerProps}>
          <Image src="/images/logo.png" alt="FixiHub Logo" maxW="240px" mb={6} mx="auto" />
          <VStack spacing={6}>
            <Heading size="lg" textAlign="center">
              Link problem
            </Heading>
            <Text textAlign="center" color="gray.600">
              The password reset link is invalid or has expired. Please request a
              new reset email.
            </Text>
            <Button
              as={RouterLink}
              to="/forgot-password"
              colorScheme="blue"
              width="full"
              size="lg"
            >
              Request new reset email
            </Button>
          </VStack>
        </Box>
      </Center>
    );
  }

  if (completed) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Box {...containerProps}>
          <Image src="/images/logo.png" alt="FixiHub Logo" maxW="240px" mb={6} mx="auto" />
          <VStack spacing={6}>
            <Heading size="lg" textAlign="center">
              Password updated
            </Heading>
            <Text textAlign="center" color="gray.600">
              Your password for <strong>{email || 'your account'}</strong> has
              been reset. You can now sign in.
            </Text>
            <Button onClick={goToLogin} colorScheme="blue" width="full" size="lg">
              Go to Login
            </Button>
          </VStack>
        </Box>
      </Center>
    );
  }

  return (
    <Center minH="100vh" bg="gray.50">
      <Box {...containerProps}>
        <Image src="/images/logo.png" alt="FixiHub Logo" maxW="240px" mb={6} mx="auto" />
        <VStack spacing={6}>
          <Heading size="lg" textAlign="center">
            Reset Password
          </Heading>
          <Text textAlign="center" color="gray.600">
            {email
              ? `Resetting password for ${email}`
              : 'Enter your new password below.'}
          </Text>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={4}>
              <FormControl id="password">
                <FormLabel>New password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a new password"
                  autoComplete="new-password"
                  size="lg"
                />
              </FormControl>

              <FormControl id="password2">
                <FormLabel>Confirm new password</FormLabel>
                <Input
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                  size="lg"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                width="full"
                isLoading={submitting}
                size="lg"
              >
                Update Password
              </Button>
            </VStack>
          </form>

          <Text mt={4} textAlign="center">
            <RouterLink to="/login" style={{ color: 'var(--chakra-colors-blue-500)', fontWeight: 'bold' }}>
              Back to login
            </RouterLink>
          </Text>
        </VStack>
      </Box>
    </Center>
  );
}

export default ResetPassword;